<template>
    <transition name="fade">
        <div v-if="error.length">
            <InitError :error />
        </div>
        <div
            v-else
            v-qa="'app-content'"
            v-qa.has.cookie.banner="Boolean(!cookiesDismissedCookie)"
            v-qa.segment.delay="JSON.stringify(segmentDelay)"
        >
            <AppContentTop />

            <main
                id="main"
                v-qa="'main'"
                class="main"
            >
                <slot name="default" />
            </main>

            <AppContentBottom />
        </div>
    </transition>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { useAnalytics } from './composables/app/gtm/useAnalytics'
import {
    defaultRegion,
    getRegionFromRoute,
    getRoutingRegion,
    useRegionPlugin,
} from '@/plugins/region'

import AppContentTop from '@/AppContentTop.vue'
import AppContentBottom from '@/AppContentBottom.vue'
import { useCookiesDismissedCookie } from '@/plugins/cookie-plugin.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { watch } from 'vue'
import InitError from '@/components/InitError.vue'
import { useResponseError } from '@/helpers/errors'
import { useLoadSegmentOnInteractionOrDelay } from '@/third-party/segment.ts'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { installInit } from '@/plugins/init/init.ts'
import { useTranslator } from '@/i18n'
import { useMenus } from '@/plugins/menu.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { useInitialUrl } from '@/plugins/current-url.ts'
import { useInitialState } from '@/plugins/initial-state.ts'
import { useApp } from '@/plugins/app.ts'
import { useGraphqlClient } from '@/plugins/graphql-client.ts'
import { useBench } from '@/plugins/bench.ts'

const {
    selectRegion,
    selectAndPersistRegion,
    hasRegionSet,
    region: regionReactive,
} = useRegionPlugin()

const redirect = useRedirect()

const router = useRouter()

const region = getRegionFromRoute(router.currentRoute.value)

const hadRegionSet = hasRegionSet.value

if (import.meta.env.SSR) {
    await selectRegion(region)
} else {
    await selectAndPersistRegion(region).then(() => {
        // Reset hasRegionSet because any other call to selectAndPersistRegion
        // Will set it to true to prevent accidental display on explicit navigation to another region
        hasRegionSet.value = hadRegionSet
    })
}

const app = useApp()
const graphqlClient = useGraphqlClient()
const appTranslator = useTranslator()
const regionPlugin = useRegionPlugin()
const currencyInput = useCurrencyInput()
const menuPlugin = useMenus()
const authPlugin = useAuth()
const isAdmin = useIsAdmin()
const initialUrl = useInitialUrl()
const authToken = initialUrl.searchParams.get('authToken')
const initialState = useInitialState()
const bench = useBench()

const init = installInit(app, graphqlClient, hadRegionSet, {
    translator: appTranslator,
    regionPlugin: regionPlugin,
    currency: currencyInput,
    menus: menuPlugin,
    auth: authPlugin,
    isAdmin: isAdmin,
    authToken: authToken,
    initialState: initialState,
    initialUrl: initialUrl,
    bench,
})

const { initQueryResponse } = init

const cookiesDismissedCookie = useCookiesDismissedCookie()

// Lighthouse stops tracking script parsing in mobile performance tests
// At about 3500-3600ish timeout
// This however varies slightly
const segmentDelay = 10_000
useLoadSegmentOnInteractionOrDelay(segmentDelay)

if (!import.meta.env.SSR) {
    watch(regionReactive, async (newRegion) => {
        if (newRegion && !newRegion.isActive) {
            const route = router.resolve({
                name: router.currentRoute.value.name,
                params: {
                    ...router.currentRoute.value.params,
                    region: getRoutingRegion(defaultRegion),
                },
            })
            await redirect(route.href, 301)
        }
    })
}

useAnalytics()

const error = useResponseError(initQueryResponse)
</script>

<style lang="scss" src="./styles/icon.scss"></style>
<style lang="scss" src="./styles/colors.scss"></style>
<style lang="scss" src="./styles/timing-functions.scss"></style>

// Unscoped styles. For elements outside the app tree (i.e. teleport targets)
<style lang="scss">
#teleport-tooltip {
    position: fixed;
    z-index: 4000;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.cookies-banner {
    position: fixed;
    bottom: 15px;
    left: 15px;
    padding: 15px 15px 10px;
    max-width: 220px;
    z-index: 1050;
    background-color: $stone-2;
    box-shadow: 0 0 15px rgba($black, 0.2);

    .message {
        margin: 0 0 10px;
        font-size: 0.875rem;
        line-height: 1.2em;

        a {
            text-decoration: underline;
        }
    }

    .cookie-button-wrapper {
        width: 100%;
        display: flex;
        justify-content: flex-end;

        .dismiss-button {
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.75rem;
            text-transform: uppercase;
            font-weight: 400;
            color: $blue;
            cursor: pointer;
        }
    }
}
</style>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.45s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
